import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { type NextApproverChoiceConstructor } from '@webapp/modules/expense/entities/NextApproverChoice'
import { type TravelAddressConstructor } from '@webapp/modules/expense/entities/TravelAddress'
import { type TravelApprovalLogConstructor } from '@webapp/modules/expense/entities/TravelApprovalLog'
import { type TravelDestinationConstructor } from '@webapp/modules/expense/entities/TravelDestination'
import { type TravelRequestDetailConstructor } from '@webapp/modules/expense/entities/TravelRequestDetail'
import {
  type TravelDetailApprovalLogApiModel,
  type TravelRequestDetailApiModel,
} from '../api-models/TravelRequestDetailApiModel'
import {
  type TravelAddressApiModel,
  type TravelDestinationApiModel,
  type TravelNextApproverChoiceApiModel,
} from '../api-models/TravelRequestSummaryApiModel'

// v1 dates are space-separated UTC strings (`2015-05-26 23:54:59+00:00`) —
// normalize to ISO so entity consumers can rely on `Date`-parseable values.
const toISODate = (value: string): ISODateString => value.replace(' ', 'T')

const mapDetailAddress = (
  address: TravelAddressApiModel
): TravelAddressConstructor => ({
  id: address.id,
  name: address.name ?? '',
  addressLineOne: address.addressLineOne ?? '',
  city: address.city ?? null,
  stateProvince: address.state_province ?? null,
  country: address.country ?? null,
  postalCode: address.postalCode ?? '',
})

const mapDetailDestination = (
  destination: TravelDestinationApiModel
): TravelDestinationConstructor => ({
  id: destination.id,
  sequence: destination.sequence,
  transport: destination.Transport,
  departurePlaceId: destination.departure,
  departureReference: destination.departureReference ?? '',
  departureDate: toISODate(destination.departureDate),
  departureAddress: mapDetailAddress(destination.departureAddress),
  arrivalPlaceId: destination.arrival,
  arrivalReference: destination.arrivalReference ?? '',
  arrivalDate: toISODate(destination.arrivalDate),
  arrivalAddress: mapDetailAddress(destination.arrivalAddress),
})

const mapDetailApprovalLog = (
  log: TravelDetailApprovalLogApiModel
): TravelApprovalLogConstructor => ({
  id: log.id,
  action: log.action,
  dateTime: toISODate(log.dateTime),
  user: {
    firstName: log.user.firstName,
    lastName: log.user.lastName,
  },
  nextApprover: log.nextApprover
    ? {
        firstName: log.nextApprover.firstName,
        lastName: log.nextApprover.lastName,
      }
    : null,
  oldTravelBudget: Number(log.oldTravelBudget),
  newTravelBudget: Number(log.newTravelBudget),
})

const mapDetailNextApproverChoice = (
  choice: TravelNextApproverChoiceApiModel
): NextApproverChoiceConstructor => ({
  id: choice.id,
  firstName: choice.firstName,
  lastName: choice.lastName,
  levelText: choice.level_text || null,
  approvalDelegatee: choice.approval_delegatee
    ? {
        id: choice.approval_delegatee.id,
        firstName: choice.approval_delegatee.firstName,
        lastName: choice.approval_delegatee.lastName,
        expectedReturnDate: choice.expected_return_date || null,
      }
    : null,
})

export const mapTravelRequestDetailApiModel = (
  detail: TravelRequestDetailApiModel
): TravelRequestDetailConstructor => ({
  id: detail.id,
  uuid: detail.uuid,
  status: detail.status,
  travelBudget: Number(detail.travelBudget),
  purpose: detail.purpose,
  details: detail.details || null,
  dateCreated: toISODate(detail.dateCreated),
  dateLastModified: toISODate(detail.dateLastModified),
  canEdit: detail.can_edit,
  canDelete: detail.can_delete,
  // Key absent from the observed v1 payload — default false
  canApprove: detail.can_approve ?? false,
  currency: mapCurrency(detail.currency),
  requester: detail.requester,
  nextApprover: detail.next_approver ?? null,
  primaryCategory: detail.branch ?? null,
  secondaryCategory: detail.department ?? null,
  secondaryCategoryId: detail.department_id,
  destinations: detail.destinations.map(mapDetailDestination),
  logs: detail.logs.map(mapDetailApprovalLog),
  nextApproverChoices: detail.next_approver_choices
    ? detail.next_approver_choices.map(mapDetailNextApproverChoice)
    : null,
})
